import fr from 'facet-rest';
import handleRedirect, { redirectToLogin, redirectToMaintenance } from 'Helpers/Redirect';
import Date from 'Components/UI/DateField';
import DateField from 'Components/ComponentLibrary/Components/Uncategorized/DateField';
import TextField from 'Components/UI/TextField';
import Checkbox from 'Components/UI/Checkbox';
import ReadOnlyField from 'Components/UI/Overrides/ReadOnlyField';
import SelectControl from 'Components/UI/SelectControl';
import { Fingerprint } from 'Integrations/Fingerprinting';
import MarkdownDisplay from 'Components/UI/MarkdownDisplay';
import {
  MASKED_PHONE_CHARS,
  NumberFormatUsd,
  PhoneMaskedTextInput,
  SSN_CHARS,
  SsnMaskedTextInput,
  TaxIDMaskedTextInput,
} from 'Components/UI/MaskedInput';

import MetaCollectionFormInline from 'Components/UI/Meta/MetaForm/MetaCollectionFormInline';
import { monthYear } from 'Consts/DateFormats';
import { moment } from 'Helpers/DateTime';
import EmptyListDescription from 'Components/ComponentLibrary/Components/DataDisplay/EmptyListDescription';
import RichTextEditor from 'Components/UI/RichTextEditor';
import RichTextDisplay from 'Components/UI/RichTextDisplay';
import ZipcodeField from 'Components/UI/ZipcodeField';
import Sentry from 'Integrations/Sentry';
import { getAPIURL } from 'Consts/sharedUris';

// TODO DVH there is currently quite a bit of duplication between this file and FacetRestBaseConfiguration.
// We will fast follow to move the duplicated overrides etc out of this file and reference the base override after
// we have successfully migrated discover to its own bundle. In the meantime we are attempting to modify existing webapp
// code as little as possible to avoid breaking the existing app functionality.

fr.config
  .addGlobalMetaOverride({ type: 'datetime' }, { type: 'date', isDateTime: true })
  .addGlobalMetaOverride({ type: 'email' }, { type: 'string' })
  .addGlobalMetaOverride({ type: 'date' }, { _component: () => Date })
  .addGlobalMetaOverride({ type: 'string' }, { _component: () => TextField })
  .addGlobalMetaOverride({ type: 'bool' }, { _component: () => Checkbox })
  .addGlobalMetaOverride({ type: 'number' }, { _component: () => TextField })
  .addGlobalMetaOverride({ hint: 'jsObject' }, { _component: () => MetaCollectionFormInline })
  .addGlobalMetaOverride({ hint: 'jsArray' }, { _component: () => MetaCollectionFormInline })
  .addGlobalMetaOverride({ hint: 'usd' }, {
    _customProps: {
      InputProps: {
        inputComponent: NumberFormatUsd,
      },
    },
  })
  .addGlobalMetaOverride({ hint: 'zipcode' }, {
    _customProps: {
      InputProps: {
        inputComponent: ZipcodeField,
      },
    },
  })
  .addGlobalMetaOverride({ hint: 'yearMonth' }, {
    _customProps: {
      openTo: 'year',
      views: ['year', 'month'],
      format: monthYear,
      placeholder: monthYear,
    },
    _component: () => DateField,
    _transformValue: () => (v) => {
      if (!v) {
        return '';
      }
      return moment(v).utc().startOf('month').toISOString();
    },
  })
  .addGlobalMetaOverride({ hint: 'phoneNumber' }, {
    _customProps: {
      InputProps: {
        inputComponent: PhoneMaskedTextInput,
      },
    },
    _formatValue: () => (v) => {
      return v.replace(MASKED_PHONE_CHARS, '');
    },
  })
  .addGlobalMetaOverride({ hint: 'ssn' }, {
    _customProps: {
      InputProps: {
        inputComponent: SsnMaskedTextInput,
      },
    },
    _formatValue: () => (v) => {
      return v.replace(SSN_CHARS, '');
    },
  })
  .addGlobalMetaOverride({ hint: 'taxId' }, {
    _customProps: {
      InputProps: {
        inputComponent: TaxIDMaskedTextInput,
      },
    },
    _formatValue: () => (v) => {
      return v.replace(SSN_CHARS, '');
    },
  })
  .addGlobalMetaOverride(
    { type: 'picklist' },
    { _component: () => SelectControl },
  )
  .addGlobalMetaOverride(
    { type: 'multipicklist' },
    { _component: () => SelectControl },
  )
  .addGlobalMetaOverride(
    { hint: 'richtext' },
    { _component: () => RichTextEditor },
  )
  .addGlobalMetaOverride(
    { readonly: true },
    { _component: () => ReadOnlyField },
    { ordinal: 100 },
  )
  .addGlobalMetaOverride(
    { hint: 'richtext', readonly: true },
    { _component: () => RichTextDisplay },
    { ordinal: 100 },
  )
  .addGlobalMetaOverride(
    { type: 'string', hint: 'markdown', readonly: true },
    { _component: () => MarkdownDisplay },
    { ordinal: 101 },
  )
  .addGlobalMetaOverride(
    { name: 'emptyListDescription', readonly: true },
    { _component: () => EmptyListDescription },
    { ordinal: 101 },
  )
  .addResponseInterceptor((res) => {
    if (res.status === 503) {
      redirectToMaintenance();
    } else if (res.status === 401) {
      redirectToLogin();
    }

    return res;
  })
  .addResponseInterceptor((res, { jsonResponseBody }) => {
    if (res.body?.data?.type === 'route') {
      handleRedirect(res.body.data);
    }
    const jsonResponseBodyData = jsonResponseBody?.data;
    if (jsonResponseBodyData !== null && jsonResponseBodyData !== undefined && typeof jsonResponseBodyData === 'object' && jsonResponseBodyData.type === 'route') {
      handleRedirect(jsonResponseBodyData);
    }

    return res;
  })
  .addResponseInterceptor(async (res, { jsonResponseBody, opts }) => {
    if (res.status === 242) {
      if (!jsonResponseBody.links.self) {
        throw new Error('no self link provided in redirect response');
      }

      try {
        // fetch resource on redirect link
        const { raw, json } = await fr.http.Get(jsonResponseBody.links.self, { cookie: opts.headers.cookie }, { returnAll: true });
        return { overrideRes: true, raw, json };
      } catch (e) {
        throw new Error(`error in redirect call err: ${e}`);
      }
    }
    return res;
  })
  .addRequestInterceptor((req) => {
    if (!req.url.startsWith(getAPIURL()) && !req.url.startsWith('/')) {
      const err = new Error(`Attempted to inject outside content from ${req.url}`);
      Sentry.createSentryErrorLog(err);
      throw err;
    }

    if (process.env.REACT_APP_COMMIT_HASH !== undefined) {
      req.opts.headers['X-UI-Commit-Hash'] = process.env.REACT_APP_COMMIT_HASH;
    }

    return req;
  })
  .addRequestInterceptor(async (req) => {
    // Only send the fingerprint to our server.
    if (!req.url.startsWith(getAPIURL())) {
      return req;
    }

    // Set the fingerprint header.
    req.opts.headers['X-DEVICE-BUNDLE-VERSION-HASH'] = await Fingerprint.getFingerprint();

    return req;
  })
  .addSilentErrorHandler((error) => {
    Sentry.createSentryErrorLog(error);
  })
  .addErrorHandler((error) => {
    Sentry.createSentryErrorLog(error, error?.status);
  })
  .addErrorHandler((error) => {
    // Show maintenance page when API is down
    if (!error?.status && error?.message?.includes('Request has been terminated')) {
      redirectToMaintenance();
    }
  });
